/**
 * Entitlements API Service - grant/deny specific pages for specific users.
 * Same apiService.get/post/delete pattern as adminApiService.ts - no custom
 * caching here, relies entirely on apiService's built-in GET cache (60s TTL,
 * invalidated automatically on any mutation whose URL starts with
 * /entitlements, which covers every endpoint below).
 */
import { apiService } from './apiService';
import type { ApiResponse } from '../types';
import type { AccessTier } from '../config/pageKeys';

export type EntitlementDecision = 'grant' | 'deny';

export interface MyEntitlement {
  page_key: string;
  decision: EntitlementDecision;
}

export interface AdminEntitlement {
  id: string;
  project_id: string;
  user_id: string;
  page_key: string;
  decision: EntitlementDecision;
  granted_by: string | null;
  reason: string | null;
  created_at: string;
  updated_at: string;
  user_email: string | null;
  user_username: string | null;
  granted_by_username: string | null;
}

export interface EntitlementsListResponse {
  entitlements: AdminEntitlement[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    total_pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

export interface BulkEntitlementResult {
  page_key: string;
  decision: EntitlementDecision;
  total_users: number;
  success_count: number;
  failed_count: number;
}

class EntitlementsApiService {
  async getMyEntitlements(): Promise<ApiResponse<{ entitlements: MyEntitlement[] }>> {
    return apiService.get('/entitlements/me');
  }

  async listEntitlements(params?: {
    page_key?: string;
    decision?: EntitlementDecision;
    page?: number;
    per_page?: number;
  }): Promise<ApiResponse<EntitlementsListResponse>> {
    const query = new URLSearchParams();
    if (params?.page_key) query.append('page_key', params.page_key);
    if (params?.decision) query.append('decision', params.decision);
    if (params?.page) query.append('page', String(params.page));
    if (params?.per_page) query.append('per_page', String(params.per_page));
    const qs = query.toString();
    return apiService.get(`/entitlements/admin${qs ? `?${qs}` : ''}`);
  }

  async setEntitlement(
    userId: string,
    pageKey: string,
    decision: EntitlementDecision,
    reason?: string
  ): Promise<ApiResponse<AdminEntitlement>> {
    return apiService.post('/entitlements/admin', { user_id: userId, page_key: pageKey, decision, reason });
  }

  async deleteEntitlement(entitlementId: string): Promise<ApiResponse<void>> {
    return apiService.delete(`/entitlements/admin/${entitlementId}`);
  }

  async bulkSetEntitlements(
    pageKey: string,
    decision: EntitlementDecision,
    opts: { userIds?: string[]; allUsers?: boolean; reason?: string }
  ): Promise<ApiResponse<BulkEntitlementResult>> {
    return apiService.post('/entitlements/admin/bulk', {
      page_key: pageKey,
      decision,
      user_ids: opts.userIds,
      all_users: opts.allUsers,
      reason: opts.reason,
    });
  }

  async getPageKeys(): Promise<ApiResponse<{ page_keys: string[] }>> {
    return apiService.get('/entitlements/admin/page-keys');
  }

  /** Public - the base public/user/admin tier for every configurable page.
   * Safe to call before login is known (needed to decide whether to even
   * attempt a 'public' tier page for a logged-out visitor). */
  async getPageLevels(): Promise<ApiResponse<{ levels: Record<string, AccessTier>; default: AccessTier }>> {
    return apiService.get('/entitlements/page-levels');
  }

  async setPageLevel(pageKey: string, level: AccessTier): Promise<ApiResponse<{ page_key: string; level: AccessTier }>> {
    return apiService.put(`/entitlements/admin/page-levels/${pageKey}`, { level });
  }
}

export const entitlementsApiService = new EntitlementsApiService();
